import { jsx as _jsx } from "react/jsx-runtime";
import { useTrack } from "@ssf/components";
import _ from "lodash";
import { ErrorResponse, Log, UserManager, } from "oidc-client-ts";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { z } from "zod";
const authority = process.env.AUTH_URL;
if (!authority) {
    throw new Error("env `AUTH_URL` is not set");
}
const client_id = process.env.AUTH_CLIENT_ID;
if (!client_id) {
    throw new Error("env `AUTH_CLIENT_ID` is not set");
}
if (process.env.MODE === "local") {
    Log.setLevel(Log.DEBUG);
    Log.setLogger(console);
}
const location = typeof window !== "undefined"
    ? window.location
    : { protocol: "", host: "", pathname: "", href: "", search: "" };
const host = `${location.protocol}//${location.host}`;
export const callbackUrls = {
    redirect_uri: "/callback",
    silent_redirect_uri: "/silent_callback",
    post_logout_redirect_uri: "/logout_callback",
};
const silentSigninRedirectAttemptKey = "auth:silent-signin-redirect-attempt";
export const isAuthCallback = () => typeof window !== "undefined" &&
    Object.values(callbackUrls).includes(window.location.pathname);
const callbackUrlsWithHost = Object.fromEntries(Object.entries(callbackUrls).map(([key, value]) => [key, host + value]));
export const userManager = new UserManager({
    authority,
    client_id,
    ...callbackUrlsWithHost,
    response_type: "code",
    scope: "openid offline_access email profile roles",
    response_mode: "query",
});
function isState(state) {
    return !!state && typeof state === "object" && "returnTo" in state;
}
(async () => {
    try {
        if (location.pathname === callbackUrls.redirect_uri) {
            const user = await userManager.signinCallback();
            const state = user?.state;
            const url = (isState(state) && state?.returnTo) || "/";
            location.href = url;
        }
        else if (location.pathname === callbackUrls.silent_redirect_uri) {
            await userManager.signinSilentCallback();
            throw new Error();
        }
        else if (location.pathname === callbackUrls.post_logout_redirect_uri) {
            // @ts-ignore
            const { state } = await userManager._client.readSignoutResponseState(location.href, true);
            const data = state?.data;
            const url = (isState(data) && data?.returnTo) || "/";
            location.href = url;
        }
    }
    catch (error) {
        const state = error instanceof ErrorResponse ? error.state : undefined;
        location.href = (isState(state) && state.returnTo) || "/";
    }
})();
const AuthContext = createContext({
    isInitiated: false,
    isLoading: false,
    signinRedirect: userManager.signinRedirect,
    signinSilent: userManager.signinSilent,
    signoutRedirect: userManager.signoutRedirect,
});
export const AuthProvider = ({ onUser, signinRedirectArgs, signoutRedirectArgs, redirectOnSilentFailure = true, ...props }) => {
    const hasTriedSigninSilent = useRef(false);
    const [state, setState] = useState({
        isLoading: false,
        isInitiated: false,
    });
    const setLoading = useCallback(async (fn) => {
        setState((prev) => ({ ...prev, isLoading: true }));
        try {
            return await fn;
        }
        finally {
            setState((prev) => ({ ...prev, isLoading: false }));
        }
    }, []);
    const setUser = useCallback(async (user) => {
        let validUser = user;
        if (user?.expired) {
            await userManager.removeUser();
            validUser = null;
        }
        onUser?.(validUser);
        const isFedAdmin = getRoles(validUser).includes("federation:1");
        setState((prev) => ({
            ...prev,
            isInitiated: true,
            user: validUser,
            isFedAdmin,
        }));
        return validUser;
    }, [onUser]);
    const signinRedirect = useCallback((args) => setLoading(userManager.signinRedirect(_.merge({ state: { returnTo: location.pathname + location.search } }, signinRedirectArgs, args))), [signinRedirectArgs, setLoading]);
    const signoutRedirect = useCallback((args) => setLoading(userManager.signoutRedirect(_.merge({ state: { returnTo: location.pathname + location.search } }, signoutRedirectArgs, args))), [signoutRedirectArgs, setLoading]);
    const signinSilent = useCallback((args) => setLoading(handleSigninSilent(args, redirectOnSilentFailure)).then(setUser), [setUser, setLoading, redirectOnSilentFailure]);
    useEffect(() => {
        const userLoaded = (user) => {
            setUser(user);
        };
        userManager.events.addUserLoaded(userLoaded);
        if (!hasTriedSigninSilent.current && !isAuthCallback()) {
            hasTriedSigninSilent.current = true;
            if (sessionStorage.getItem(silentSigninRedirectAttemptKey)) {
                sessionStorage.removeItem(silentSigninRedirectAttemptKey);
                void userManager
                    .getUser()
                    .then(setUser)
                    .catch(() => setUser(null));
            }
            else {
                void signinSilent().catch(() => setUser(null));
            }
        }
        return () => {
            userManager.events.removeUserLoaded(userLoaded);
        };
    }, [setUser, signinSilent]);
    const { identify } = useTrack();
    useEffect(() => {
        if (state.isInitiated) {
            identify(state.user ? `memdb_${state.user.profile.sub}` : undefined);
        }
    }, [state.user, state.isInitiated, identify]);
    return (_jsx(AuthContext.Provider, { value: useMemo(() => ({ ...state, signinRedirect, signinSilent, signoutRedirect }), [state, signinRedirect, signinSilent, signoutRedirect]), ...props }));
};
const handleSigninSilent = async (args, redirectOnSilentFailure) => {
    try {
        return await userManager.signinSilent(args);
    }
    catch {
        await userManager.removeUser();
        if (!redirectOnSilentFailure) {
            return null;
        }
        sessionStorage.setItem(silentSigninRedirectAttemptKey, "true");
        try {
            await userManager.signinRedirect({
                ...args,
                prompt: "none",
                state: { returnTo: location.pathname + location.search },
            });
        }
        catch (error) {
            sessionStorage.removeItem(silentSigninRedirectAttemptKey);
            throw error;
        }
        return null;
    }
};
export const useAuth = () => useContext(AuthContext);
const rolesSchema = z.array(z.string());
export const getRoles = (user) => {
    const parsedRoles = rolesSchema.safeParse(user?.profile.roles);
    return parsedRoles.success ? parsedRoles.data : [];
};
//# sourceMappingURL=AuthProvider.js.map